<template>
    <!-- Desktop Navigation -->
    <nav v-if="!isMobile" class="desktop-nav">
        <div class="nav-links">
            <router-link to="/dashboard" class="nav-link" :class="{ active: isActive('/dashboard') }">Dashboard</router-link>
            <router-link to="/tasks" class="nav-link" :class="{ active: isActive('/tasks') }">Tasks</router-link>
            <router-link to="/directory" class="nav-link" :class="{ active: isActive('/directory') }">Directory</router-link>
            <router-link to="/visitors" class="nav-link">Visitors</router-link>
            <router-link to="/goals" class="nav-link">Goals</router-link>
            <router-link to="/past-events" class="nav-link">Past Events</router-link>
            <div class="dropdown" v-if="isAdmin">
                <span class="nav-link">Event Admin</span>
                <div class="dropdown-content">
                    <router-link to="/admin/events" class="dropdown-item">Events</router-link>
                    <router-link to="/admin/venues" class="dropdown-item">Venues</router-link>
                    <router-link to="/admin/meeting-formats" class="dropdown-item">Meeting Formats</router-link>
                    <router-link to="/admin/speaker-roster" class="dropdown-item">Speaker Roster</router-link>
                </div>
            </div>
            <router-link v-if="isAdmin" to="/admin" class="nav-link">Member Admin</router-link>
            <router-link v-if="isAdmin" to="/email-templates" class="nav-link">Email Templates</router-link>
        </div>
    </nav>

    <!-- Mobile Navigation -->
    <nav v-else class="mobile-nav">
        <div class="mobile-header">
            <img src="@/assets/action-logo.svg" alt="Action Network Logo" class="logo">
            <button 
                class="hamburger-btn"
                @click="toggleMenu"
                aria-label="Toggle menu"
            >
                <span class="material-icons">menu</span>
            </button>
        </div>

        <!-- Mobile Menu Overlay -->
        <div class="mobile-menu" :class="{ 'show': isMenuOpen }">
            <router-link to="/dashboard" class="nav-link" @click="toggleMenu">Dashboard</router-link>
            <router-link to="/tasks" class="nav-link" @click="toggleMenu">Tasks</router-link>
            <router-link to="/directory" class="nav-link" @click="toggleMenu">Directory</router-link>
            <router-link to="/visitors" class="nav-link" @click="toggleMenu">Visitors</router-link>
            <router-link to="/goals" class="nav-link" @click="toggleMenu">Goals</router-link>
            <router-link to="/past-events" class="nav-link" @click="toggleMenu">Past Events</router-link>
            <template v-if="isAdmin">
                <div class="mobile-submenu">
                    <div class="submenu-header">Event Admin</div>
                    <router-link to="/admin/events" class="nav-link submenu-item" @click="toggleMenu">Events</router-link>
                    <router-link to="/admin/speaker-roster" class="nav-link submenu-item" @click="toggleMenu">Speaker Roster</router-link>
                    <router-link to="/admin/venues" class="nav-link submenu-item" @click="toggleMenu">Venues</router-link>
                    <router-link to="/admin/meeting-formats" class="nav-link submenu-item" @click="toggleMenu">Meeting Formats</router-link>
                </div>
                <router-link to="/admin" class="nav-link" @click="toggleMenu">Member Admin</router-link>
                <router-link to="/email-templates" class="nav-link" @click="toggleMenu">Email Templates</router-link>
            </template>
            <div class="mobile-divider"></div>
            <router-link to="/profile" class="nav-link" @click="toggleMenu">Profile</router-link>
            <div 
                class="menu-item" 
                @click="addToHomeScreen"
            >
                <i class="material-icons">add_to_home_screen</i>
                Add to Home Screen
            </div>
            <div class="menu-item" @click="handleLogout">
                <i class="material-icons">logout</i>
                Logout
            </div>
        </div>
    </nav>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';

export default {
    name: 'MainNavigation',
    emits: ['logout'],
    props: {
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const router = useRouter();
        const route = useRoute();
        const isMobile = ref(window.innerWidth <= 768);
        const isMenuOpen = ref(false);
        const deferredPrompt = ref(null);
        const showInstallPrompt = ref(false);
        const isIOS = ref(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);

        const checkMobile = () => {
            isMobile.value = window.innerWidth <= 768;
        };

        const toggleMenu = () => {
            isMenuOpen.value = !isMenuOpen.value;
        };

        const handleLogout = async () => {
            try {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                emit('logout');
                router.push('/login');
            } catch (error) {
                console.error('Logout error:', error);
            }
        };

        const addToHomeScreen = async () => {
            // If we have the native install prompt (mainly Chrome on Android), use it
            if (deferredPrompt.value) {
                try {
                    deferredPrompt.value.prompt();
                    await deferredPrompt.value.userChoice;
                    deferredPrompt.value = null;
                    showInstallPrompt.value = false;
                    return;
                } catch (error) {
                    console.error('Error showing install prompt:', error);
                    // Fall through to manual instructions if the prompt fails
                }
            }

            // Show platform-specific instructions
            if (isIOS.value) {
                alert('To add to home screen:\n\n' +
                      '1. Tap the Share button in Safari (rectangle with arrow pointing up)\n' +
                      '2. Scroll down and tap "Add to Home Screen"\n' +
                      '3. Tap "Add" in the top right');
            } else if (navigator.userAgent.toLowerCase().includes('android')) {
                alert('To add to home screen:\n\n' +
                      '1. Open your browser\'s menu (usually three dots or lines)\n' +
                      '2. Look for "Install app" or "Add to Home screen"\n' +
                      '3. Follow the prompts to add Action Network to your home screen\n\n' +
                      'Note: The exact steps may vary depending on your browser');
            } else {
                alert('To add to home screen, use your browser\'s "Add to Home Screen" option from the menu.');
            }
        };

        const isActive = (path) => {
            return route.path === path || route.path.startsWith(path + '/');
        };

        onMounted(() => {
            window.addEventListener('resize', checkMobile);
            window.addEventListener('beforeinstallprompt', (e) => {
                e.preventDefault();
                deferredPrompt.value = e;
                showInstallPrompt.value = true;
            });
        });

        onUnmounted(() => {
            window.removeEventListener('resize', checkMobile);
        });

        return {
            isMobile,
            isMenuOpen,
            toggleMenu,
            handleLogout,
            deferredPrompt,
            showInstallPrompt,
            isIOS,
            addToHomeScreen,
            isActive
        };
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

/* Desktop Styles */
.desktop-nav {
    background: white;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
}

.nav-links {
    display: flex;
    gap: 2rem;
    align-items: center;
}

/* Mobile Styles */
.mobile-nav {
    position: relative;
    overflow-x: hidden;  /* Prevent horizontal scroll */
}

.mobile-header {
    background: white;
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    box-sizing: border-box;
}

.logo {
    height: 35px;
    width: auto;
}

.hamburger-btn {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
}

.material-icons {
    font-size: 24px;
    color: #2c3e50;
}

.mobile-menu {
    display: none;
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    z-index: 100;
}

.mobile-menu.show {
    display: flex;
}

.mobile-divider {
    height: 1px;
    background: #eee;
    margin: 0.5rem 0;
}

/* Common Styles */
.nav-link {
    color: #486581;
    text-decoration: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: background 0.2s;
}

.nav-link:hover {
    background: #f0f4f8;
}

.nav-link.router-link-active {
    color: #4285f4;
    background: #ebf5ff;
}

/* Desktop Dropdown Styles */
.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown-content {
    display: none;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    min-width: 160px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border-radius: 4px;
    z-index: 1;
}

.dropdown:hover .dropdown-content {
    display: block;
}

.dropdown-item {
    color: #486581;
    text-decoration: none;
    padding: 0.5rem 1rem;
    display: block;
    text-align: center;
}

.dropdown-item:hover {
    background: #f0f4f8;
}

.menu-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 15px;
    cursor: pointer;
    text-align: center;
    color: #4a5568;
    text-decoration: none;
    font-size: 1rem;
}

.menu-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.material-icons {
    font-size: 20px;
}

/* Make sure mobile menu items are all centered */
.mobile-menu .menu-item,
.mobile-menu .nav-link {
    justify-content: center;
    text-align: center;
    width: 100%;
}

.mobile-submenu {
    margin-top: 1rem;
}

.submenu-header {
    font-weight: bold;
    margin-bottom: 0.5rem;
}

.submenu-item {
    padding: 0.5rem 1rem;
}
</style>